import { collectDescendantEmployeeIds } from "./reportsTeamScope";
import {
  CLIENT_MANAGER_ROLE_ID,
  EMPLOYEE_ROLE_ID,
  filterManagerAccordionRows,
} from "./timesheetReportUtils";

export const REVENUE_BY_EMPLOYEE_REPORT_KEY = "Revenue by Associate (Employee)";

export { filterManagerAccordionRows };

function employeeDisplayName(emp) {
  return `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
}

function buildEmployeeNameIndex(activeEmployees = []) {
  const byName = new Map();
  activeEmployees.forEach((emp) => {
    const name = employeeDisplayName(emp).toLowerCase();
    if (name) byName.set(name, String(emp.employee_id));
  });
  return byName;
}

function resolveEmployeeIdFromRow(row, nameIndex) {
  if (row?.employee_id != null && row.employee_id !== "") {
    return String(row.employee_id);
  }
  const name = String(row?.employee_name || row?.employee || "")
    .trim()
    .toLowerCase();
  return name ? nameIndex.get(name) || null : null;
}

function parseRevenueAmount(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  const normalized = String(value)
    .replace(/[$,\s]/g, "")
    .trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseCount(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function sumRevenueRows(rows = []) {
  let num_customers = 0;
  let num_payments_done = 0;
  let num_pending_invoices = 0;
  let amount_collected = 0;
  let amount_pending = 0;

  rows.forEach((row) => {
    num_customers += parseCount(row?.num_customers);
    num_payments_done += parseCount(row?.num_payments_done);
    num_pending_invoices += parseCount(row?.num_pending_invoices);
    amount_collected += parseRevenueAmount(row?.amount_collected);
    amount_pending += parseRevenueAmount(row?.amount_pending);
  });

  return {
    num_customers,
    num_payments_done,
    num_pending_invoices,
    amount_collected: amount_collected.toFixed(2),
    amount_pending: amount_pending.toFixed(2),
  };
}

function buildEmptyRevenueRow(employeeName, employeeId = null) {
  return {
    employee_id: employeeId,
    employee_name: employeeName,
    num_customers: 0,
    num_payments_done: 0,
    num_pending_invoices: 0,
    amount_collected: "0.00",
    amount_pending: "0.00",
  };
}

export function buildManagerAccordionRevenueRows(
  flatRows = [],
  activeEmployees = [],
) {
  if (!Array.isArray(activeEmployees) || !activeEmployees.length) {
    return [];
  }

  const nameIndex = buildEmployeeNameIndex(activeEmployees);
  const rowByEmployeeId = new Map();

  (flatRows || []).forEach((row) => {
    const employeeId = resolveEmployeeIdFromRow(row, nameIndex);
    if (!employeeId) return;
    rowByEmployeeId.set(employeeId, { ...row, employee_id: employeeId });
  });

  const managers = activeEmployees
    .filter((emp) => Number(emp.role_id) === CLIENT_MANAGER_ROLE_ID)
    .sort((a, b) =>
      employeeDisplayName(a).localeCompare(employeeDisplayName(b)),
    );

  return managers.map((manager) => {
    const managerId = String(manager.employee_id);
    const teamIds = collectDescendantEmployeeIds(activeEmployees, managerId, {
      includeSelf: false,
    });

    const children = activeEmployees
      .filter(
        (emp) =>
          teamIds.has(String(emp.employee_id)) &&
          Number(emp.role_id) === EMPLOYEE_ROLE_ID,
      )
      .map((emp) => {
        const empId = String(emp.employee_id);
        const existing = rowByEmployeeId.get(empId);
        return (
          existing || buildEmptyRevenueRow(employeeDisplayName(emp), empId)
        );
      })
      .sort((a, b) =>
        String(a.employee_name || "").localeCompare(String(b.employee_name || "")),
      );

    const managerOwn = rowByEmployeeId.get(managerId);
    const summarySource = [
      ...children,
      ...(managerOwn ? [managerOwn] : []),
    ];

    return {
      managerId,
      employee_name: employeeDisplayName(manager),
      direct_reports_count: children.length,
      ...sumRevenueRows(summarySource),
      children,
    };
  });
}
